import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import api from '../../services/api';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Star, Trash2, User, Building } from 'lucide-react';

const AdminReviews = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [deleteModal, setDeleteModal] = useState({ open: false, review: null });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchReviews();
  }, [user, navigate, statusFilter, typeFilter, page]);

  const fetchReviews = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('review_type', typeFilter);
      params.append('skip', page * 50);
      params.append('limit', 50);

      const res = await api.get(`/admin/reviews?${params.toString()}`);
      setReviews(res.data.reviews);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.review) return;
    try {
      await api.delete(`/admin/reviews/${deleteModal.review.id}`);
      toast.success('Review deleted');
      setDeleteModal({ open: false, review: null });
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const getRatingStars = (rating) => {
    return Array(5).fill(0).map((_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < rating ? 'text-amber-400 fill-amber-400' : 'text-[#5B6472]'}`} />
    ));
  };

  return (
    <AdminLayout title="Reviews" breadcrumbs={[{ label: 'Reviews' }]}>
      <div className="space-y-6">
        {/* Filters */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                <SelectItem value="all" className="text-[#0A0F1E]">All Status</SelectItem>
                <SelectItem value="pending" className="text-[#0A0F1E]">Pending</SelectItem>
                <SelectItem value="completed" className="text-[#0A0F1E]">Completed</SelectItem>
                <SelectItem value="acknowledged" className="text-[#0A0F1E]">Acknowledged</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px] bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-lg">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                <SelectItem value="all" className="text-[#0A0F1E]">All Types</SelectItem>
                <SelectItem value="one-time" className="text-[#0A0F1E]">One-time</SelectItem>
                <SelectItem value="weekly" className="text-[#0A0F1E]">Weekly</SelectItem>
                <SelectItem value="monthly" className="text-[#0A0F1E]">Monthly</SelectItem>
                <SelectItem value="quarterly" className="text-[#0A0F1E]">Quarterly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Reviews Table */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F5F8FF]/50">
                <tr>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Employee</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Employer</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Type</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Rating</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Status</th>
                  <th className="text-left p-4 text-[#5B6472] text-sm font-medium">Date</th>
                  <th className="text-right p-4 text-[#5B6472] text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8EDF5]">
                {reviews.map((review) => (
                  <tr key={review.id} className="hover:bg-[#F5F8FF]/30">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <User className="w-4 h-4 text-[#1B5AFF]" />
                        <span className="text-[#0A0F1E]">{review.employee_name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <Building className="w-4 h-4 text-blue-400" />
                        <span className="text-[#0A0F1E]">{review.employer_name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs capitalize">
                        {review.review_type}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-0.5">{getRatingStars(review.performance_rating || 0)}</div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        review.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        review.status === 'acknowledged' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-amber-500/20 text-amber-400'
                      }`}>
                        {review.status || 'pending'}
                      </span>
                    </td>
                    <td className="p-4 text-[#5B6472] text-sm">
                      {review.created_at ? new Date(review.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-4">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-400 hover:text-red-300"
                        onClick={() => setDeleteModal({ open: true, review })}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {reviews.length === 0 && !loading && (
            <div className="p-12 text-center">
              <Star className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
              <p className="text-[#5B6472]">No reviews found</p>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-[#E8EDF5]">
            <p className="text-[#5B6472] text-sm">Total: {total} reviews</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="border-[#E8EDF5] text-[#0A0F1E]">Previous</Button>
              <Button size="sm" variant="outline" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * 50 >= total} className="border-[#E8EDF5] text-[#0A0F1E]">Next</Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Delete Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, review: null })}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]">
          <DialogHeader>
            <DialogTitle className="text-red-400">Delete Review</DialogTitle>
          </DialogHeader>
          <p className="text-[#5B6472]">
            Are you sure you want to delete this review for {deleteModal.review?.employee_name}?
            <strong className="text-red-400"> This action cannot be undone.</strong>
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteModal({ open: false, review: null })} className="text-[#5B6472]">Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 hover:bg-red-700 text-[#FFFFFF]">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminReviews;
